import * as api from "@/api/api";
import React, { createContext, useContext, useReducer } from "react";
import { cleanupAnonymizeValueIfNoPermissions } from "@/anonymization/useAnonymize";

type ThaloWebContextType = {
  selectedOrganization?: any;
  setSelectedOrganization?: any;
  selectedLocation?: any;
  setSelectedLocation?: any;
  showLocationSwitcher?: boolean;
  setShowLocationSwitcher?: any;
  resetNoDataCharts?: any;
  addNoDataChart?: any;
  removeNoDataChart?: any;
  noDataCharts?: any[];
  showSidebar?: boolean;
  setShowSidebar?: any;
  displaySidebar?: boolean;
  setDisplaySidebar?: any;
};

const ThaloWebContext = createContext<ThaloWebContextType>({});

type OrganizationLocationState = {
  selectedOrganization?: any;
  selectedLocation?: any
}

type Action =
  | { type: 'set_location', location: any }
  | { type: 'set_organization', organization: any };

const organizationLocationReducer = (state: OrganizationLocationState, action: Action): OrganizationLocationState => {
  switch (action.type) {
    case "set_organization":
      return {
        ...state,
        selectedOrganization: action.organization,
        selectedLocation: action.organization?.location_set?.[0],
      }
    case "set_location":
      return {
        ...state,
        selectedLocation: action.location
      }
  }
}

export function ThaloWebContextProvider({ children }: any) {
  const [showLocationSwitcher, setShowLocationSwitcher] = React.useState(true);
  const [showSidebar, setShowSidebar] = React.useState(false);
  const [displaySidebar, setDisplaySidebar] = React.useState(true);

  const [{
    selectedLocation,
    selectedOrganization,
  }, dispatch] = useReducer(organizationLocationReducer, {})
  const setSelectedOrganization = (organization: any) => dispatch({ type: "set_organization", organization });
  const setSelectedLocation = (location: any) => dispatch({ type: "set_location", location })

  const [noDataCharts, setNoDataCharts] = React.useState<string[]>([]);

  const resetNoDataCharts = () => {
    setNoDataCharts([]);
  };

  const addNoDataChart = (query: string) => {
    setNoDataCharts((prev) => {
      if (!prev.includes(query)) {
        return [...prev, query];
      }
      return prev;
    });
  };

  const removeNoDataChart = (query: string) => {
    setNoDataCharts((prev) => {
      return prev.filter((p) => p !== query);
    });
  };

  const user = api.useUser();

  React.useEffect(() => {
    if (user.data) {
      cleanupAnonymizeValueIfNoPermissions(user.data);
    }
  }, [user.data]);

  React.useEffect(() => {
    const anyLocationSelected = selectedLocation || selectedOrganization;
    if (anyLocationSelected && noDataCharts.length) {
      resetNoDataCharts();
    }
  }, [selectedLocation, selectedOrganization]);

  return (
    <ThaloWebContext.Provider
      value={{
        selectedOrganization,
        setSelectedOrganization,
        selectedLocation,
        setSelectedLocation,
        showLocationSwitcher,
        setShowLocationSwitcher,
        noDataCharts,
        resetNoDataCharts,
        addNoDataChart,
        removeNoDataChart,
        showSidebar,
        setShowSidebar,
        displaySidebar,
        setDisplaySidebar,
      }}
    >
      {children}
    </ThaloWebContext.Provider>
  );
}

export function useThaloWeb() {
  const context = useContext(ThaloWebContext);
  if (context === undefined) {
    throw new Error("Context must be used within a Provider");
  }
  return context;
}
